import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useModal } from '../context/ModalContext';

import './ISI.css';

// Full US Prescribing Information (opens in a new tab), mirroring EluciremUSA.com
const PI_URL = 'https://www.elucirem.us/sites/default/files/2026-02/elucirem-uspi-2026_6.pdf';

const BOTTOM_THRESHOLD = 24; // px from the bottom that counts as "reached the bottom"

const ISI = () => {
    // Hide the sticky bar while a comparison / submenu popup is open so it
    // never overlays the modal (the modal is a focused, full-screen view).
    const modalOpen = useModal();
    const [expanded, setExpanded] = useState(false);
    const contentRef = useRef(null);
    const wasAtBottomRef = useRef(false);
    const lastYRef = useRef(0);
    const cooldownRef = useRef(0); // suppress auto toggles briefly after any change

    const revealContent = useCallback(() => {
        // Bring the freshly opened ISI copy into view (it expands downward,
        // growing the page rather than covering the content above).
        window.setTimeout(() => {
            if (contentRef.current) {
                contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 60);
    }, []);

    // Auto-expand when the user reaches the bottom of the page, and auto-collapse
    // when they scroll back up out of the ISI section. A short cooldown after each
    // change (plus expand-on-crossing / collapse-on-scroll-up-only) prevents the
    // grow/shrink of the page from oscillating the state.
    useEffect(() => {
        if (modalOpen) {
            return undefined;
        }

        const handleScroll = () => {
            const now = Date.now();
            const y = window.scrollY;
            const goingUp = y < lastYRef.current - 2;
            lastYRef.current = y;

            const doc = document.documentElement;
            const scrollable = doc.scrollHeight > window.innerHeight + 8;
            const atBottom = window.innerHeight + y >= doc.scrollHeight - BOTTOM_THRESHOLD;

            if (now >= cooldownRef.current) {
                if (!expanded) {
                    if (scrollable && atBottom && !wasAtBottomRef.current) {
                        setExpanded(true);
                        cooldownRef.current = now + 500;
                        revealContent();
                    }
                } else if (goingUp && contentRef.current) {
                    // Collapse once the ISI copy has been scrolled back down past
                    // the middle of the screen (i.e. the user has left the section).
                    const rect = contentRef.current.getBoundingClientRect();
                    if (rect.top > window.innerHeight * 0.5) {
                        setExpanded(false);
                        cooldownRef.current = now + 500;
                    }
                }
            }

            wasAtBottomRef.current = scrollable && atBottom;
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [modalOpen, expanded, revealContent]);

    if (modalOpen) {
        return null;
    }

    const handleToggle = () => {
        cooldownRef.current = Date.now() + 600; // don't let auto-scroll undo a manual click
        setExpanded((open) => {
            const next = !open;
            if (next) {
                revealContent();
            }
            return next;
        });
    };

    return (
        <aside
            className={expanded ? 'isi-bar isi-bar--open' : 'isi-bar'}
            aria-label="Important Safety Information"
        >
            <div className="isi-content-wrap">
                <div className="isi-content" ref={contentRef} role="region" aria-label="Important Safety Information details">
                    <div className="isi-boxed-warning">
                        <h3>WARNING: RISK ASSOCIATED WITH INTRATHECAL USE and NEPHROGENIC SYSTEMIC FIBROSIS (NSF)</h3>
                        <h4>Risk Associated with Intrathecal Use</h4>
                        <p>Intrathecal administration of gadolinium-based contrast agents (GBCAs) can cause serious adverse reactions including death, coma, encephalopathy, and seizures. ELUCIREM is not approved for intrathecal use.</p>
                        <h4>Nephrogenic Systemic Fibrosis</h4>
                        <p>GBCAs increase the risk for NSF among patients with impaired elimination of the drugs. Avoid use of ELUCIREM in these patients unless the diagnostic information is essential and not available with non-contrasted MRI or other modalities. NSF may result in fatal or debilitating fibrosis affecting the skin, muscle and internal organs.</p>
                        <p>The risk for NSF appears highest among patients with:</p>
                        <ul>
                            <li>Chronic, severe kidney disease (GFR &lt;30 mL/min/1.73 m&sup2;), or</li>
                            <li>Acute kidney injury.</li>
                        </ul>
                        <p>Screen patients for acute kidney injury and other conditions that may reduce renal function. For patients at risk for chronically reduced renal function (for example, age &gt;60 years, hypertension or diabetes), estimate the glomerular filtration rate (GFR) through laboratory testing.</p>
                        <p>For patients at highest risk for NSF, do not exceed the recommended ELUCIREM dose and allow a sufficient period of time for elimination of the drug from the body prior to any re-administration.</p>
                    </div>

                    <h3>Indications and Usage</h3>
                    <p>ELUCIREM&trade; (gadopiclenol) injection is indicated in adult and pediatric patients, including term neonates for use with magnetic resonance imaging (MRI) to detect and visualize lesions with abnormal vascularity in the central nervous system (brain, spine, and associated tissues), and the body (head and neck, thorax, abdomen, pelvis, and musculoskeletal system).</p>

                    <h3>Contraindications</h3>
                    <p>Contraindicated in patients with history of hypersensitivity reactions to ELUCIREM.</p>

                    <h3>Warnings and Precautions</h3>
                    <p><strong>Risk Associated with Intrathecal Use:</strong> Intrathecal administration of GBCAs can cause serious adverse reactions including death, coma, encephalopathy, and seizures. The safety and effectiveness of ELUCIREM have not been established with intrathecal use. ELUCIREM is not approved for intrathecal use.</p>
                    <p><strong>Nephrogenic Systemic Fibrosis:</strong> GBCAs increase the risk for NSF among patients with impaired elimination of the drugs. Avoid use of ELUCIREM among these patients unless the diagnostic information is essential and not available with non-contrast MRI or other modalities.</p>
                    <p><strong>Hypersensitivity Reactions:</strong> With GBCAs, serious hypersensitivity reactions have occurred. Before ELUCIREM administration, assess all patients for any history of a reaction to contrast media, bronchial asthma and/or allergic disorders.</p>
                    <p><strong>Gadolinium Retention:</strong> Gadolinium is retained in the body for months or years in several organs. While clinical consequences of gadolinium retention have not been established in patients with normal renal function, certain patients might be at higher risk. These include patients requiring multiple lifetime doses, pregnant and pediatric patients, and patients with inflammatory conditions. Minimize repetitive GBCA imaging studies, particularly closely spaced studies when possible.</p>
                    <p><strong>Acute Kidney Injury:</strong> In patients with chronically reduced renal function, acute kidney injury requiring dialysis has occurred with the use of GBCAs.</p>
                    <p><strong>Extravasation and Injection Site Reactions:</strong> Injection site pain have been reported in the clinical studies with ELUCIREM. Extravasation during ELUCIREM administration may result in tissue irritation. Ensure catheter and venous patency before the injection of ELUCIREM.</p>
                    <p><strong>Interference with Visualization of Lesions Visible with Non-Contrast MRI:</strong> As with any GBCA, ELUCIREM may impair the visualization of lesions seen on non-contrast MRI.</p>

                    <h3>Adverse Reactions</h3>
                    <p>In clinical trials, the most frequent adverse reactions that occurred in &gt; 0.2% of patients who received ELUCIREM included: injection site pain, headache, nausea, injection site warmth, injection site coldness, dizziness, localized swelling and erythema.</p>
                    <p><strong>Postmarketing Experience with use of ELUCIREM or other GBCAs:</strong> Acute pancreatitis, acute respiratory distress syndrome, pulmonary edema.</p>

                    <h3>Use in Specific Populations</h3>
                    <p><strong>Pregnancy:</strong> GBCAs cross the human placenta and result in fetal exposure.</p>
                    <p><strong>Lactation:</strong> There are no data on the presence of ELUCIREM in human milk, the effects on the breastfed infant, or the effects on milk production.</p>
                    <p><strong>Pediatric Use:</strong> The safety and effectiveness of ELUCIREM for use with MRI to detect and visualize lesions with abnormal vascularity have been established in pediatric patients including term neonates. The safety of ELUCIREM has not been established in preterm neonates.</p>
                    <p><strong>Geriatric Use:</strong> Elucirem is known to be substantially excreted by the kidney, and the risk of adverse reactions may be greater in patients with impaired renal function.</p>
                    <p><strong>Renal Impairment:</strong> Avoid use of GBCAs among patients with renal impairment unless the diagnostic information is essential and not available with non-contrast MRI or other modalities.</p>

                    <p>You are encouraged to report negative side effects of prescription drugs to the FDA. Visit <a href="https://www.fda.gov/medwatch" target="_blank" rel="noopener noreferrer">www.fda.gov/medwatch</a> or call 1-800-FDA-1088.</p>
                    <p>Please see the full <a href={PI_URL} target="_blank" rel="noopener noreferrer">Prescribing Information</a>, including the Medication Guide, for additional important safety information.</p>
                </div>
            </div>

            <div className="isi-header">
                <span className="isi-title">ELUCIREM&trade; (gadopiclenol) injection Important Safety Information</span>
                <div className="isi-actions">
                    <a className="isi-pi-link" href={PI_URL} target="_blank" rel="noopener noreferrer">See Full Prescribing Information</a>
                    <button
                        type="button"
                        className="isi-toggle"
                        aria-expanded={expanded}
                        onClick={handleToggle}
                    >
                        {expanded ? 'Collapse ▾' : 'Expand ▴'}
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default ISI;
